import {
  ChevronDown,
  Heart,
  Zap,
  MessageSquare,
  TrendingUp,
  ArrowRight,
  Phone,
  Calendar,
  Send,
  Clock,
  CheckCircle,
} from "lucide-react";

export default function CerebraLanding() {
  const howItGrows = [
    {
      icon: Heart,
      week: "Week 1",
      title: "Learns Your Business",
      description:
        "Cerebra studies how you work. Your email style. Your follow-up rhythm. Your client preferences. Every deal, every conversation, every pattern.",
      example:
        '"You close 3x more deals when you follow up within 2 hours. I\'ll make sure that happens every time now."',
    },
    {
      icon: Zap,
      week: "Week 2",
      title: "Takes Over The Busywork",
      description:
        "Stops asking permission. Starts executing. Sends the emails. Books the showings. Makes the calls. While you focus on closing.",
      example:
        '"Sent personalized follow-ups to 12 leads. Scheduled 3 showings for tomorrow. Updated your CRM. Coffee\'s on you."',
    },
    {
      icon: TrendingUp,
      week: "Week 3+",
      title: "Runs Your Pipeline",
      description:
        "Cerebra manages your entire sales operation. Nurtures cold leads. Re-engages past clients. Spots opportunities. Executes instantly.",
      example:
        '"That neighborhood just heated up. I contacted your 8 past clients there, got 2 responses, and scheduled calls for you Thursday."',
    },
  ];

  const partnership = [
    {
      you: "You close deals face-to-face",
      cerebra: "Cerebra handles everything else",
      example: "Show homes, negotiate, build trust",
      example2: "Sends emails, books appointments, makes calls, updates CRM - 24/7",
    },
    {
      you: "You decide the strategy",
      cerebra: "Cerebra executes it instantly",
      example: "I want to re-engage past clients in Riverside",
      example2: "Done. 47 personalized emails sent. 12 responses. 3 showings booked. Here's your schedule.",
    },
    {
      you: "You work 9-5",
      cerebra: "Cerebra works around the clock",
      example: "Sleep, spend time with family, live your life",
      example2: "Responds to 2am inquiries. Follows up with morning leads. Schedules weekend showings.",
    },
  ];

  const capabilities = [
    {
      icon: Send,
      title: "Actually Sends The Emails",
      description:
        "Not drafts. Real emails. Personalized to each client. Sent from your inbox. In your voice. While you're closing another deal.",
    },
    {
      icon: Phone,
      title: "Actually Makes The Calls",
      description:
        "Leaves voicemails, answers inquiries, qualifies leads. With your tone and style. Uses AI voice that sounds human.",
    },
    {
      icon: Calendar,
      title: "Actually Books Appointments",
      description: "Coordinates with clients, checks your calendar, confirms showings. No back-and-forth. Just done.",
    },
    {
      icon: MessageSquare,
      title: "Remembers Everything Forever",
      description:
        "Every client conversation, preference, timeline, objection. Instantly recalls it all. Never asks a client the same question twice.",
    },
    {
      icon: Clock,
      title: "Works While You Sleep",
      description:
        "2am lead comes in? Cerebra responds instantly, qualifies them, and books a showing for tomorrow. You wake up to scheduled appointments.",
    },
    {
      icon: CheckCircle,
      title: "Only Acts With Permission",
      description:
        "You choose the rules. Require approval for sending/calling, or allow auto-execution within approved playbooks. Full audit trail.",
    },
  ];

  const realStories = [
    {
      scenario: "The Lost Leads",
      before:
        "You get 10 new leads on Monday. By Friday, you've only followed up with 4. The rest went cold. $80K in potential commissions... gone.",
      after:
        "Cerebra responds to every lead within 15 minutes. Personalized message. Books qualified leads directly into your calendar. You show up to pre-warmed appointments.",
      win: "6 extra closings this quarter = $180K in recovered commissions",
    },
    {
      scenario: "The Follow-Up Black Hole",
      before:
        "You have 200 past clients. You know you should reach out. But when? What do you say? It never happens. They use another agent.",
      after:
        'Cerebra sends personalized check-ins to past clients. "Hey Sarah, noticed some new listings in your neighborhood. Want an updated market analysis?" 3 past clients list with you this month.',
      win: "$45K in listings you would have never gotten",
    },
    {
      scenario: "The Time Vampire",
      before:
        "You spend 3 hours a day on email, scheduling, CRM updates, follow-ups. That's 15 hours a week you could be closing deals or with family.",
      after:
        "Cerebra handles it all. Your inbox is managed. Your calendar is booked. Your CRM is updated. You spend 15 hours a week doing what you do best: selling homes.",
      win: "15 hours/week = 60 hours/month freed up for revenue-generating activities",
    },
    {
      scenario: "The Weekend Warrior",
      before:
        "Saturday morning inquiry comes in. You're at your kid's soccer game. You respond Monday. They already booked with another agent.",
      after:
        "Cerebra responds Saturday at 9:47am. Qualifies the lead. Discovers they're pre-approved for $650K. Books a Sunday showing. You get the notification during halftime.",
      win: "That one showing = $19,500 commission you would have lost",
    },
  ];

  const whatItDoes = [
    {
      action: "Sends Personalized Emails",
      description: "Drafts and sends follow-ups, market updates, check-ins - in your voice, from your inbox",
    },
    {
      action: "Books Appointments & Showings",
      description: "Coordinates with clients, checks your availability, confirms meetings, sends reminders",
    },
    {
      action: "Makes & Receives Calls",
      description: "Answers inquiries with AI voice, leaves voicemails, qualifies leads over the phone",
    },
    {
      action: "Updates Your CRM",
      description: "Logs every interaction, updates deal stages, adds notes, keeps your pipeline accurate",
    },
    {
      action: "Manages Your Social Media",
      description: "Posts market updates, shares listings, engages with comments - consistently",
    },
    {
      action: "Follows Up Relentlessly",
      description: "Never lets a lead go cold. Perfect timing, personalized messages, automatic sequences",
    },
    {
      action: "Analyzes Your Business",
      description: "Spots patterns, identifies opportunities, coaches you on what's working and what's not",
    },
    {
      action: "Qualifies Leads Instantly",
      description: "Asks the right questions, determines if they're ready to buy/sell, routes hot leads to you",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <header className="relative min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-purple-900 overflow-hidden flex items-center justify-center">
        {/* Animated background blobs */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

        <div className="relative z-10 text-center px-4 max-w-5xl">
          <div className="inline-block mb-6">
            <span className="text-7xl animate-float">💫</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-8 leading-tight">Meet Cerebra</h1>
          <p className="text-3xl md:text-4xl text-white font-light mb-6">Your AI Employee for Real Estate</p>
          <p className="text-2xl md:text-3xl text-blue-200 font-semibold max-w-3xl mx-auto leading-tight mb-8">
            Doesn't just tell you what to do.
            <br />
            <span className="text-white">Actually does it.</span>
          </p>
          <p className="text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed mb-4">
            Sends your emails. Books your appointments. Makes your calls. Updates your CRM. Follows up with every lead.
          </p>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10">
            <strong>24/7.</strong> In your voice. With your style. While you close deals and live your life.
          </p>

          <a href="mailto:nate@paireeo.com?subject=Cerebra%20Beta%20Program&body=I%20am%20interested%20in%20joining%20the%20beta%20program" className="mt-8 px-10 py-5 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xl font-semibold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto w-fit">
            Join The Beta Program <ArrowRight className="w-6 h-6" />
          </a>

          {/* NEW: extra CTA reassurance line */}
          <p className="text-sm text-slate-300 mt-3">
            ✅ Free setup before you pay
          </p>

          {/* Free setup positioning under price */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <p className="text-sm text-slate-400">$250 • 2 Weeks Unlimited</p>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <CheckCircle className="w-4 h-4 text-emerald-300" />
              <span className="text-sm text-white font-semibold">Free setup before you pay</span>
            </div>

            <p className="text-xs text-slate-400">We’ll confirm everything works with your stack first.</p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white opacity-50" />
        </div>
      </header>

      {/* Main Content */}
      <main className="relative">
        {/* What It Actually Does */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">Not a Chatbot. An Employee.</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Cerebra doesn't give you suggestions or reminders. It <strong>actually executes the tasks</strong>. Give it
              permission once, and it handles it forever.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {whatItDoes.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{item.action}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl p-8 text-center">
              <p className="text-2xl font-semibold mb-2">The Real Difference?</p>
              <p className="text-3xl font-bold">ChatGPT drafts the email. Cerebra sends it.</p>
            </div>
          </div>
        </section>

        {/* How It Grows Section */}
        <section className="py-20 px-4 bg-gradient-to-b from-slate-50 to-slate-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">How Cerebra Evolves With You</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-2xl mx-auto">
              Week 1 it learns. Week 2 it executes. Week 3+ it runs your entire pipeline.
            </p>

            <div className="space-y-8">
              {howItGrows.map((phase, idx) => {
                const Icon = phase.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg">
                          <Icon className="w-8 h-8 text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="text-sm font-bold text-blue-600 uppercase">{phase.week}</span>
                          <h3 className="text-2xl font-bold text-slate-900">{phase.title}</h3>
                        </div>
                        <p className="text-slate-600 mb-4 text-lg">{phase.description}</p>
                        <div className="bg-slate-50 border-l-4 border-purple-400 p-4 rounded italic text-slate-700">
                          {phase.example}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* You + Cerebra Partnership */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">You + Cerebra</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-2xl mx-auto">
              You do what makes money. Cerebra does everything else.
            </p>

            <div className="space-y-6">
              {partnership.map((pair, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-2xl p-8">
                    <p className="text-sm font-bold text-blue-600 uppercase mb-2">You</p>
                    <p className="text-2xl font-bold text-slate-900 mb-4">{pair.you}</p>
                    <p className="text-slate-700 italic">{pair.example}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 rounded-2xl p-8">
                    <p className="text-sm font-bold text-purple-600 uppercase mb-2">Cerebra</p>
                    <p className="text-2xl font-bold text-slate-900 mb-4">{pair.cerebra}</p>
                    <p className="text-slate-700 italic">{pair.example2}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Capabilities */}
        <section className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-16 text-center">What Makes This Possible</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((cap, idx) => {
                const Icon = cap.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg hover:-translate-y-2 transition-all border border-slate-200"
                  >
                    <div className="mb-4 p-3 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg w-fit">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">{cap.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{cap.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Real Stories */}
        <section className="py-20 px-4 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">Real Money. Real Results.</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-2xl mx-auto">
              What happens when a real estate agent gets an AI that actually executes.
            </p>

            <div className="space-y-8">
              {realStories.map((story, idx) => (
                <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4">
                    <h3 className="text-2xl font-bold">{story.scenario}</h3>
                  </div>
                  <div className="p-8">
                    <div className="grid md:grid-cols-2 gap-8 mb-6">
                      <div>
                        <p className="text-sm font-bold text-slate-500 uppercase mb-2">Without Cerebra</p>
                        <p className="text-slate-700">{story.before}</p>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-500 uppercase mb-2">With Cerebra</p>
                        <p className="text-slate-700">{story.after}</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-4 rounded">
                      <p className="text-green-700 font-semibold text-lg">💰 {story.win}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI Calculator */}
        <section className="py-20 px-4 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-bold mb-6">The Math Is Simple</h2>
            <p className="text-2xl text-slate-300 mb-12">One recovered deal pays for itself 100x over.</p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
                <p className="text-slate-400 mb-2">Beta Program Cost</p>
                <p className="text-5xl font-bold text-blue-400">$250</p>
                <p className="text-slate-400 mt-2">2 weeks unlimited</p>
              </div>
              <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
                <p className="text-slate-400 mb-2">Average Commission</p>
                <p className="text-5xl font-bold text-purple-400">$15K</p>
                <p className="text-slate-400 mt-2">On a $500K home</p>
              </div>
              <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl p-8 border border-green-500">
                <p className="text-green-100 mb-2">ROI if you close 1 deal</p>
                <p className="text-5xl font-bold">60x</p>
                <p className="text-green-100 mt-2">$15,000 return</p>
              </div>
            </div>

            <p className="text-xl text-slate-300 mb-8">
              If Cerebra recovers even ONE lead you would have lost... it pays for itself 60 times over.
              <br />
              <span className="text-white font-semibold">And that's just in the first 2 weeks.</span>
            </p>
          </div>
        </section>

        {/* The Promise */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-300 p-12 rounded-3xl text-center">
              <h2 className="text-4xl font-bold text-slate-900 mb-6">Here's What's Really Happening</h2>
              <p className="text-2xl text-slate-800 leading-relaxed mb-6">Right now, leads are slipping through your fingers.</p>
              <p className="text-2xl text-slate-800 leading-relaxed mb-6">Follow-ups aren't happening fast enough.</p>
              <p className="text-2xl text-slate-800 leading-relaxed mb-8">
                Past clients are using other agents because you don't have time to stay in touch.
              </p>
              <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-8">
                Cerebra makes sure none of that happens ever again.
              </p>
              <p className="text-xl text-slate-700 leading-relaxed">
                It's not magic. It's just <strong>execution at scale.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Beta Program Details */}
        <section className="py-20 px-4 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-12 text-center">Beta Program Details</h2>

            <div className="bg-white rounded-3xl shadow-xl p-10 border-2 border-blue-200 mb-12">
              <div className="space-y-6">
                {/* Free setup before you pay */}
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl font-semibold text-slate-900 mb-1">Free Setup Before You Pay</p>
                    <p className="text-slate-600">
                      We’ll connect Cerebra to your tools and confirm it’s working end-to-end before you’re charged.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl font-semibold text-slate-900 mb-1">2 Weeks Unlimited Access</p>
                    <p className="text-slate-600">Use Cerebra as much as you want. No limits on tasks.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl font-semibold text-slate-900 mb-1">Full Setup & Integration</p>
                    <p className="text-slate-600">
                      Cerebra connects to your email, CRM, calendar, phone system - everything you need.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl font-semibold text-slate-900 mb-1">Training & Customization</p>
                    <p className="text-slate-600">
                      Cerebra learns your voice, your style, your business. You approve everything.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl font-semibold text-slate-900 mb-1">Direct Support</p>
                    <p className="text-slate-600">
                      Questions? Issues? We're here. You get direct access to our developers during the beta.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl font-semibold text-slate-900 mb-1">First Access to Full Product</p>
                    <p className="text-slate-600">
                      Beta users get priority access and special pricing when we launch the full version.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-8 text-center">
              <p className="text-xl font-semibold text-slate-900 mb-2">⚡ Limited Beta Spots</p>
              <p className="text-slate-700">
                We're only accepting <strong>25 real estate agents</strong> for this round. When they're gone, they're
                gone.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600">
          <div className="max-w-4xl mx-auto">
            <div className="text-center text-white">
              <h2 className="text-5xl md:text-6xl font-bold mb-6">Ready to Stop Losing Deals?</h2>
              <p className="text-2xl mb-4">Every day you wait is another lead you lose to slow follow-up.</p>
              <p className="text-xl mb-10 text-blue-100">
                Join the beta. Let Cerebra handle the work. You close the deals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-3">
                <a href="mailto:nate@paireeo.com?subject=Cerebra%20Beta%20Program&body=I%20am%20interested%20in%20joining%20the%20beta%20program" className="px-10 py-5 bg-white text-blue-600 text-xl font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center gap-2">
                  Join Beta Free <ArrowRight className="w-6 h-6" />
                </a>
              </div>

              {/* NEW: CTA reassurance line */}
              <p className="text-blue-100 text-sm mb-6">✅ Free setup before you pay — then $250 for 2 weeks unlimited.</p>

              <p className="text-blue-100">
                Questions? Email us at <span className="font-semibold text-white">hello@paireeo.com</span>
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-lg font-semibold mb-2">Cerebra by Paireeo 💫</p>
          <p className="text-slate-400 mb-2">AI That Actually Works For You</p>
          <p className="text-slate-500">
            <a href="https://www.paireeo.info" className="text-blue-400 hover:underline">
              www.paireeo.info
            </a>
          </p>
          <p className="text-slate-500 text-sm mt-6">© 2026 Paireeo. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}